import { Box, Combobox, Group, Pill, Text, TextInput, Tooltip, useCombobox } from "@mantine/core";
import { ITag } from "@mctypes/global.types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { sortArrayByString } from "../app/utils";
import "./Tags/Tags.css";

const stop = (e: React.SyntheticEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const TAGS_OPEN_EVENT = "mc:tags-open";

const sameSetCI = (a: string[], b: string[]) => {
  const norm = (x: string) => x.trim().toLowerCase();
  const as = a.map(norm).filter(Boolean).sort();
  const bs = b.map(norm).filter(Boolean).sort();
  if (as.length !== bs.length) return false;
  for (let i = 0; i < as.length; i++) if (as[i] !== bs[i]) return false;
  return true;
};

const Tags: React.FC<{
  tags: string[];
  fetchUniqTags: (...args: string[]) => Promise<ITag[]>;
  fetchArgs: string[];
  updateCb: (tags: string[]) => void;
  showAddButton?: boolean;
}> = ({ tags, fetchUniqTags, fetchArgs, updateCb, showAddButton }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const idRef = useRef<string>(crypto.randomUUID());

  const initialValueRef = useRef<string[]>(tags ?? []);
  const dirtyRef = useRef(false);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();

      // ✅ Commit only once, on close (prevents ag-grid rerender during multi-add)
      if (dirtyRef.current) {
        dirtyRef.current = false;
        updateCb(value);
      }

      setSearch("");
    },
  });

  const [value, setValue] = useState<string[]>(tags ?? []);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // close if another Tags instance opens
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ id: string }>).detail;
      if (detail?.id !== idRef.current) {
        setSearch("");
        combobox.closeDropdown();
      }
    };
    window.addEventListener(TAGS_OPEN_EVENT, handler as EventListener);
    return () => window.removeEventListener(TAGS_OPEN_EVENT, handler as EventListener);
  }, [combobox]);

  // Keep in sync when grid updates from outside (only when not editing)
  useEffect(() => {
    if (!combobox.dropdownOpened) {
      setValue(tags ?? []);
      initialValueRef.current = tags ?? [];
      dirtyRef.current = false;
    }
  }, [tags, combobox.dropdownOpened]);

  const showIcon = showAddButton ?? true;

  const visible = useMemo(() => value.slice(0, 3), [value]);
  const hidden = useMemo(() => value.slice(3), [value]);
  const hiddenCount = hidden.length;

  const ensureSuggestions = async () => {
    if (suggestions.length > 0) return;
    try {
      setLoading(true);
      const allUniqTags = await fetchUniqTags(...fetchArgs);
      const opts = sortArrayByString(allUniqTags, "_id").map((t) => t._id);
      setSuggestions(opts);
    } finally {
      setLoading(false);
    }
  };

  const announceOpen = () => {
    window.dispatchEvent(new CustomEvent(TAGS_OPEN_EVENT, { detail: { id: idRef.current } }));
  };

  const openDropdown = async () => {
    announceOpen();
    await ensureSuggestions();

    // snapshot starting value for this edit session
    initialValueRef.current = value;
    dirtyRef.current = false;

    combobox.openDropdown();
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const markDirtyIfChanged = (next: string[]) => {
    dirtyRef.current = !sameSetCI(initialValueRef.current, next);
  };

  const setDraft = (next: string[]) => {
    // normalize + case-insensitive uniqueness
    const norm = (t: string) => t.trim();
    const cleaned = next.map(norm).filter(Boolean);

    const unique: string[] = [];
    for (const t of cleaned) {
      if (!unique.some((x) => x.toLowerCase() === t.toLowerCase())) unique.push(t);
    }

    setValue(unique);
    markDirtyIfChanged(unique);
  };

  const commitDraftAdd = (raw: string) => {
    const nextTag = raw.trim();
    if (!nextTag) return;

    if (value.some((t) => t.toLowerCase() === nextTag.toLowerCase())) {
      setSearch("");
      return;
    }

    setDraft([nextTag, ...value]);
    setSearch("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const removeTagDraft = (tag: string) => {
    setDraft(value.filter((t) => t !== tag));
  };

  const filteredOptions = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = suggestions.filter((t) => !value.some((v) => v.toLowerCase() === t.toLowerCase()));
    if (!s) return base;
    return base.filter((t) => t.toLowerCase().includes(s));
  }, [suggestions, search, value]);

  const canCreate =
    search.trim().length > 0 &&
    !suggestions.some((t) => t.toLowerCase() === search.trim().toLowerCase()) &&
    !value.some((t) => t.toLowerCase() === search.trim().toLowerCase());

  return (
    <Combobox
      store={combobox}
      withinPortal
      onOptionSubmit={(opt) => {
        if (opt.startsWith("__create__:")) commitDraftAdd(opt.replace("__create__:", ""));
        else commitDraftAdd(opt);
      }}
    >
      <Combobox.Target>
        {combobox.dropdownOpened ? (
          <Box onMouseDown={stop} onClick={stop}>
            <TextInput
              ref={inputRef}
              value={search}
              onMouseDown={stop}
              onClick={stop}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={loading ? "Laster tags…" : "Skriv og trykk Enter…"}
              size="xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitDraftAdd(search);
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  combobox.closeDropdown(); // will trigger commit-on-close if dirty
                }
                if (e.key === "Backspace" && search.length === 0 && value.length > 0) {
                  e.preventDefault();
                  removeTagDraft(value[value.length - 1]);
                }
              }}
              styles={{
                input: { height: 26, minHeight: 26, paddingTop: 0, paddingBottom: 0 },
              }}
            />
          </Box>
        ) : (
          <Box
            onMouseDown={stop}
            onClick={async (e) => {
              stop(e);
              await openDropdown();
            }}
            style={{ cursor: "pointer" }}
          >
            <Group gap={6} wrap="nowrap" align="center">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Pill.Group gap={4} style={{ minWidth: 0, overflow: "hidden", alignItems: "center" }}>
                  {visible.length === 0 ? (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      Legg til tag…
                    </Text>
                  ) : (
                    visible.map((t) => (
                      <Pill key={t} size="sm" className="mc-tag-pill">
                        <Text size="sm" lineClamp={1}>
                          {t}
                        </Text>
                      </Pill>
                    ))
                  )}

                  {hiddenCount > 0 && (
                    <Tooltip label={hidden.join(", ")} multiline position="bottom-start" withArrow>
                      <Pill size="sm" className="mc-tag-pill">
                        <Text size="sm">+{hiddenCount}</Text>
                      </Pill>
                    </Tooltip>
                  )}
                </Pill.Group>
              </Box>
            </Group>
          </Box>
        )}
      </Combobox.Target>

      <Combobox.Dropdown
        onMouseDown={stop}
        onClick={stop}
        className="mc-tags-dropdown"
        style={{
          maxHeight: "40vh",
          overflowY: "auto",
          padding: 0, // cleaner when you have your own padding inside
        }}
      >
        {/* Sticky selected tags header */}
        {value.length > 0 && (
          <Box
            px={8}
            pt={6}
            pb={4}
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "var(--mantine-color-body)",
              borderBottom: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Pill.Group gap={4}>
              {value.map((t) => (
                <Pill key={t} size="sm" withRemoveButton onRemove={() => removeTagDraft(t)} className="mc-tag-pill">
                  <Text size="sm">{t}</Text>
                </Pill>
              ))}
            </Pill.Group>
          </Box>
        )}

        <Box px={8} py={6}>
          <Combobox.Options>
            {loading ? (
              <Combobox.Empty>Laster tags…</Combobox.Empty>
            ) : (
              <>
                {filteredOptions.map((t) => (
                  <Combobox.Option key={t} value={t} onMouseDown={stop}>
                    {t}
                  </Combobox.Option>
                ))}

                {canCreate && (
                  <Combobox.Option value={`__create__:${search.trim()}`} onMouseDown={stop}>
                    Opprett: <strong>{search.trim()}</strong>
                  </Combobox.Option>
                )}

                {filteredOptions.length === 0 && !canCreate && <Combobox.Empty>Ingen treff</Combobox.Empty>}
              </>
            )}
          </Combobox.Options>
        </Box>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default Tags;
